import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { useRole } from '../contexts/RoleContext';

const RoleProtectedRoute = ({ children, requiredPermission, fallbackPath = '/dashboard' }) => {
  const { userRole, checkPermission, isLoading, getRoleDisplayInfo } = useRole();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          p: 4
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50' }}>
            Loading...
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>
            Checking your access permissions...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!userRole) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has permission for this specific permission or any of the permissions in array
  const hasPermission = Array.isArray(requiredPermission) 
    ? requiredPermission.some(perm => checkPermission(perm))
    : checkPermission(requiredPermission);
    
  if (requiredPermission && !hasPermission) {
    const roleInfo = getRoleDisplayInfo();
    
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          p: 4
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxWidth: 500,
            width: '100%'
          }}
        >
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem'
              }
            }}
          >
            Access Denied
          </Alert>
          
          <Typography variant="h5" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
            Insufficient Permissions
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: '#6c757d', lineHeight: 1.6 }}>
            You don't have permission to access this section.
          </Typography>
          
          {roleInfo && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#495057', mb: 1 }}>
                Your Role: {roleInfo.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                {roleInfo.description}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" sx={{ mt: 2, color: '#6c757d' }}>
            Please contact your administrator if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return children;
};

export default RoleProtectedRoute;
